import { useEffect, useState } from "react";
import { Gift, RotateCcw, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Field } from "@/components/common";
import { adminApi, errMsg } from "@/lib/api";
import { toast } from "sonner";

function Summary({ d, chosen, dirty }) {
  if (!d.defined && !dirty) return <p className="text-sm" data-testid="trial-modules-status">Ainda não definido: novos cadastros recebem <b>todos os módulos</b>, como hoje.</p>;
  return (
    <div className="space-y-2" data-testid="trial-modules-status">
      <p className="text-sm">{dirty ? "Depois de salvar, novos cadastros receberão:" : "Novos cadastros recebem:"}</p>
      <div className="flex flex-wrap gap-1.5" data-testid="trial-modules-summary">
        {chosen.length ? chosen.map((m) => <span key={m.id} className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">{m.label}</span>)
          : <span className="text-xs text-muted-foreground">Nenhum módulo opcional (só Painel, Configurações e Planos)</span>}
      </div>
    </div>
  );
}

// Mesmos limites da edição de planos pagos (0 = ilimitado). Antes da 1ª gravação, o formulário começa como um plano novo.
const LIMITS = [["users", "Limite de usuários"], ["products", "Limite de produtos"], ["orders", "Limite de pedidos por mês"], ["stores", "Quantidade de lojas"]];
const LIMITS_NEW = { users: 0, products: 0, orders: 0, stores: 1 };
const sameLimits = (a, b) => LIMITS.every(([k]) => Number(a[k]) === Number(b[k]));

export function TrialModulesBox() {
  const [d, setD] = useState(null);
  const [sel, setSel] = useState([]);
  const [lim, setLim] = useState(LIMITS_NEW);
  const [pub, setPub] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = (data) => {
    setD(data); setSel(data.modules ?? data.catalog.map((m) => m.id));
    setLim(data.limits ?? LIMITS_NEW); setPub(!!data.public);
  };
  useEffect(() => { adminApi.get("/trial-modules").then((r) => load(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!d) return null;
  const saved = d.modules ?? d.catalog.map((m) => m.id);
  const modsDirty = sel.length !== saved.length || sel.some((m) => !saved.includes(m));
  const limDirty = !d.limits_defined || !sameLimits(lim, d.limits);
  const pubDirty = pub !== !!d.public;
  const dirty = modsDirty || limDirty || pubDirty;
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const setL = (k) => (e) => setLim((x) => ({ ...x, [k]: e.target.value }));
  const put = async (body, msg) => {
    setBusy(true);
    try { const { data } = await adminApi.put("/trial-modules", body); load(data); toast.success(msg); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  // Módulos só vão junto se mudaram ou já estavam definidos: salvar só os limites não "define" os módulos sozinho.
  const save = () => put({
    ...(modsDirty || d.defined ? { modules: sel } : {}),
    limits: Object.fromEntries(LIMITS.map(([k]) => [k, Math.max(0, parseInt(lim[k], 10) || 0)])),
    public: pub,
  }, "Teste grátis salvo");
  return (
    <div className="card-surface space-y-4 p-6" data-testid="trial-modules-box">
      <div className="flex items-start gap-3">
        <Gift className="mt-0.5 h-5 w-5 text-primary" />
        <div>
          <p className="font-semibold">Módulos do teste grátis</p>
          <p className="text-xs text-muted-foreground">Vale só para empresas cadastradas depois de salvar. Empresas já cadastradas não mudam. Fiscal e Ponto continuam dependendo das liberações de homologação. "PDV / Caixa" inclui abertura e fechamento de caixa, operadores, sangria e suprimento. Para uma empresa específica, use Clientes → Módulos e valores.</p>
        </div>
      </div>
      <Summary d={d} chosen={d.catalog.filter((m) => sel.includes(m.id))} dirty={dirty} />
      <div className="grid overflow-hidden rounded-lg border sm:grid-cols-2">
        {d.catalog.map((m) => (
          <label key={m.id} className="flex cursor-pointer items-center gap-3 border-b px-4 py-2.5 text-sm last:border-b-0" data-testid={`trial-module-row-${m.id}`}>
            <Switch checked={sel.includes(m.id)} onCheckedChange={() => toggle(m.id)} data-testid={`trial-module-${m.id}`} />
            <span className="flex-1">{m.label}</span>
          </label>
        ))}
      </div>
      <div className="space-y-3" data-testid="trial-limits-box">
        <p className="text-sm font-medium">Limites do teste grátis</p>
        {!d.limits_defined && <p className="text-xs text-muted-foreground" data-testid="trial-limits-status">Ainda não definidos: novos cadastros ficam sem limite, como hoje. Os valores abaixo são só sugestão e passam a valer quando você salvar.</p>}
        <div className="grid gap-4 sm:grid-cols-2">
          {LIMITS.map(([k, label]) => (
            <Field key={k} label={label} hint="0 = ilimitado"><Input type="number" min={0} value={lim[k]} onChange={setL(k)} data-testid={`trial-limit-${k}-input`} /></Field>
          ))}
        </div>
        <label className="flex items-start gap-3 rounded-lg border p-3 text-sm">
          <Switch checked={pub} onCheckedChange={setPub} className="mt-0.5" data-testid="trial-public-switch" />
          <span><b>Visível na página de planos</b><span className="block text-xs text-muted-foreground">Ligado, o teste grátis aparece como um card na página de planos do site, com os módulos e os limites acima. Desligado, o card não aparece.</span></span>
        </label>
      </div>
      <div className="flex flex-wrap gap-3">
        <Button onClick={save} disabled={busy || (d.defined && !dirty)} className="h-11" data-testid="trial-modules-save-btn"><Save className="mr-2 h-4 w-4" />Salvar teste grátis</Button>
        {d.defined && <Button variant="outline" onClick={() => put({ modules: null }, "Módulos do teste grátis liberados")} disabled={busy} className="h-11" data-testid="trial-modules-reset-btn"><RotateCcw className="mr-2 h-4 w-4" />Voltar a liberar tudo (sem configuração)</Button>}
      </div>
    </div>
  );
}
